import React from 'react';
import { useNavigate, useParams } from 'react-router';
import type {
  MLRecommendation,
  Opportunity,
  OpportunityCandidate,
} from '@axiom/validation';
import { CandidateOpportunityUtils } from '@axiom/utils';
import {
  CandidateProfileUtil,
  ImageCircle,
  Layout,
  LayoutItem,
  ParagraphHeader,
  TilesItem,
  Tiles,
} from '@axiom/ui';

import { OTCandidateName } from './OpportunityTalentStyles';
import { OpportunityTalentRateMargin } from './OpportunityTalentRateMargin';
import { OpportunityTalentTileBadges } from './OpportunityTalentTileBadges';

export type OpportunityTalentParentPositionListProps = {
  opportunity: Opportunity;
  opportunityCandidates: OpportunityCandidate[];
  isTalentPool?: boolean;
  mlRecommendations?: MLRecommendation[];
};

export const OpportunityTalentParentPositionList = ({
  opportunity,
  opportunityCandidates,
  isTalentPool,
  mlRecommendations,
}: OpportunityTalentParentPositionListProps) => {
  const navigate = useNavigate();
  const params = useParams();

  return (
    <Tiles columnWidth={1} name="talent-list">
      {opportunityCandidates.map(opportunityCandidate => {
        const hasSubmittedRate =
          CandidateOpportunityUtils.hasSubmittedRate(opportunityCandidate);

        return (
          <TilesItem
            key={opportunityCandidate.id}
            name={opportunityCandidate.id}
            selected={params.viewId === opportunityCandidate.id}
            onClick={() => {
              void navigate(
                `/opportunity-detail/${opportunity.id}/talent/${opportunityCandidate.candidateId}`
              );
            }}
          >
            <Layout direction="vertical" verticalGutter="8px">
              <Layout position="top space-between">
                <Layout position="middle">
                  <LayoutItem rightGutter="8px">
                    <ImageCircle
                      name={`${opportunityCandidate.id}-image`}
                      size="small"
                      src={CandidateProfileUtil.getProfileImageUri(
                        {
                          profileImageKey: opportunityCandidate.profileImageKey,
                        },
                        'W_56'
                      )}
                      imageName={opportunityCandidate.calculatedDisplayName}
                    />
                  </LayoutItem>
                  <LayoutItem stretched>
                    <OTCandidateName>
                      <ParagraphHeader maxLines={1} name="candidate-name">
                        {opportunityCandidate.calculatedDisplayName}
                      </ParagraphHeader>
                    </OTCandidateName>
                    {!isTalentPool || hasSubmittedRate ? (
                      <OpportunityTalentRateMargin
                        opportunityCandidate={opportunityCandidate}
                        opportunity={opportunity}
                      />
                    ) : null}
                  </LayoutItem>
                </Layout>
                <OpportunityTalentTileBadges
                  isPriorityTalent={opportunityCandidate.isPriorityTalent}
                  opportunity={opportunity}
                  opportunityCandidateOrMLRecommendation={opportunityCandidate}
                />
              </Layout>
            </Layout>
          </TilesItem>
        );
      })}

      {mlRecommendations
        ? mlRecommendations.map(recommendation => {
            return (
              <TilesItem
                key={recommendation.id}
                name={recommendation.id}
                selected={params.viewId === recommendation.candidateId}
                onClick={() => {
                  void navigate(
                    `/opportunity-detail/${opportunity.id}/talent/${recommendation.candidate.id}`
                  );
                }}
              >
                <Layout
                  direction="vertical"
                  verticalGutter="8px"
                  name="ml-recommendation-layout"
                >
                  <Layout position="top space-between">
                    <Layout position="middle">
                      <LayoutItem rightGutter="8px">
                        <ImageCircle
                          name={`${recommendation.id}-image`}
                          size="small"
                          src={CandidateProfileUtil.getProfileImageUri(
                            {
                              profileImageKey:
                                recommendation.candidate.profileImageKey,
                            },
                            'W_56'
                          )}
                          imageName={
                            recommendation.candidate.calculatedDisplayName
                          }
                        />
                      </LayoutItem>
                      <LayoutItem stretched>
                        <OTCandidateName>
                          <ParagraphHeader maxLines={1} name="candidate-name">
                            {recommendation.candidate.calculatedDisplayName}
                          </ParagraphHeader>
                        </OTCandidateName>
                        <OpportunityTalentTileBadges
                          isPriorityTalent={
                            recommendation.candidate.isPriorityTalent
                          }
                          opportunity={opportunity}
                          opportunityCandidateOrMLRecommendation={
                            recommendation
                          }
                        />
                      </LayoutItem>
                    </Layout>
                  </Layout>
                </Layout>
              </TilesItem>
            );
          })
        : []}
    </Tiles>
  );
};
